import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import creditApi from '../services/creditApi';
import { errorHandler } from '../utils/errorHandler';

/**
 * Credit store for managing user credits state
 */
export const useCreditStore = defineStore('credits', () => {
  // State
  const balance = ref(0);
  const invitationCredits = ref(0);
  const referralCredits = ref(0);
  const history = ref([]);
  const isLoading = ref(false);
  const error = ref(null);
  const lastUpdated = ref(null);

  // Getters
  const currentBalance = computed(() => balance.value);
  const totalCredits = computed(() => balance.value + invitationCredits.value + referralCredits.value);
  const hasEnoughCredits = computed(() => {
    return (amount) => balance.value >= amount;
  });
  const canUseService = computed(() => balance.value >= 3); // 3 credits for humorous resume
  const creditError = computed(() => error.value);
  const isLowBalance = computed(() => balance.value < 3);

  // Actions
  /**
   * Fetch current credit balance
   */
  const fetchBalance = async () => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const response = await creditApi.getBalance();
      // API interceptor returns response.data directly, so response is already the data object
      if (response && response.success) {
        balance.value = response.credits || 0;
        invitationCredits.value = response.invitationCredits || 0;
        referralCredits.value = response.referralCredits || 0;
        lastUpdated.value = new Date();
        return response;
      } else {
        throw new Error(response?.error || 'Failed to fetch balance');
      }
    } catch (err) {
      error.value = errorHandler.createUserMessage(err);
      console.error('[CreditStore] Error fetching balance:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Refresh balance (alias for fetchBalance)
   */
  const refreshBalance = async () => {
    return await fetchBalance();
  };

  /**
   * Fetch transaction history
   * @param {object} filters - Filter options
   */
  const fetchHistory = async (filters = {}) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const response = await creditApi.getHistory(filters);
      // API interceptor returns response.data directly, so response is already the data object
      if (response && response.success) {
        history.value = response.transactions || [];
        return {
          transactions: response.transactions,
          pagination: response.pagination
        };
      } else {
        throw new Error(response?.error || 'Failed to fetch history');
      }
    } catch (err) {
      error.value = errorHandler.createUserMessage(err);
      console.error('[CreditStore] Error fetching history:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Update balance from API response (e.g., after spending credits)
   * @param {number} newBalance - New balance value
   */
  const updateBalance = (newBalance) => {
    balance.value = newBalance;
    lastUpdated.value = new Date();
  };

  /**
   * Clear all credit data
   */
  const clearCredits = () => {
    balance.value = 0;
    invitationCredits.value = 0;
    referralCredits.value = 0;
    history.value = [];
    error.value = null;
    lastUpdated.value = null;
  };

  return {
    // State
    balance,
    invitationCredits,
    referralCredits,
    history,
    isLoading,
    error,
    lastUpdated,
    
    // Getters
    currentBalance,
    totalCredits,
    hasEnoughCredits,
    canUseService,
    creditError,
    isLowBalance,
    
    // Actions
    fetchBalance,
    refreshBalance,
    fetchHistory,
    updateBalance,
    clearCredits
  };
});
